<div class="dpa-page">

  <!-- ============ THE MASTHEAD · ONE CARD ============
       Identity, the figure run and the window on ONE surface, the High Risk
       board's anatomy: three stacked cards read as chrome three times over.
       A hairline divides the accuracy figures from the coverage ones. -->
  <div class="dpa-masthead">
    <div class="dpa-top">
      <div class="dpa-title">
        <div class="dpa-title__text">
          <!-- The chip lives INSIDE the heading. As a sibling of the text block
               it was centred on title+date together, which parked it a third of
               a line below the title it belongs to. -->
          <h1><span class="dpa-title__chip" aria-hidden="true"><i class="fa fa-bullseye"></i></span>Discharge Analytics</h1>
          <!-- One line labels the window AND the scope: the bands and the table
               below both follow the ticked buildings, and the caption is the
               only place that says so in words. Absent when every chip is on. -->
          <p class="dpa-title__meta" *ngIf="report && !loading">
            {{ report.from }} &ndash; {{ report.to }}<ng-container *ngIf="isFacilityFiltered"> &middot; {{ scopeCaption }}</ng-container>
          </p>
          <!-- Looking back lives here; planning forward lives on the Discharge
               Plan screen, which links here the same way. -->
          <a class="dpa-back" [routerLink]="plannerUrl" [attr.aria-label]="backLabel">
            <span class="dpa-back__chev" aria-hidden="true">&lsaquo;</span>
            <span>{{ backLabel }}</span>
          </a>
        </div>
      </div>

      <!-- In the identity row, not in a band of its own. The question the band
           answers is written above it, and every figure carries its meaning in
           one visible line: the labels alone did not say what was being counted. -->
      <div class="dpa-band" *ngIf="!loading && !error && report">
        <p class="dpa-band__cap" id="dpa-lead-cap">Was each discharge anticipated in the plan two days ahead?</p>
        <dl class="dpa-figs" aria-labelledby="dpa-lead-cap">
          <div class="dpa-fig" *ngFor="let figure of leadFigures; trackBy: trackByFigure"
            [class.dpa-fig--good]="figure.tone === 'good'"
            [class.dpa-fig--warn]="figure.tone === 'warn'">
            <dt>{{ figure.label }}</dt>
            <dd>{{ figure.value }}</dd>
            <dd class="dpa-fig__sub">{{ figure.sub }}</dd>
          </div>
        </dl>
      </div>

      <!-- The window, as three choices rather than two empty date fields. The
           browser's mm/dd/yyyy chrome only appears once somebody asks for it. -->
      <div class="dpa-window">
        <div class="dpa-seg" role="group" aria-label="Report window">
          <button type="button" *ngFor="let option of windowModes; trackBy: trackByMode"
            class="dpa-seg__btn" [class.is-on]="mode === option.id"
            [attr.aria-pressed]="mode === option.id"
            [disabled]="loading"
            (click)="selectMode(option.id)">{{ option.label }}</button>
        </div>

        <!-- The labels sit ABOVE their fields rather than inside them. Inside,
             a label competed with the placeholder, the divider and the
             browser's calendar glyph for about eighty pixels. -->
        <ng-container *ngIf="mode === 'custom'">
          <label class="dpa-field">
            <span class="dpa-field__label">From</span>
            <input type="date" id="dpa-from" name="dpa-from" [(ngModel)]="from" (keyup.enter)="applyWindow()">
          </label>
          <label class="dpa-field">
            <span class="dpa-field__label">To</span>
            <input type="date" id="dpa-to" name="dpa-to" [(ngModel)]="to" (keyup.enter)="applyWindow()">
          </label>
          <button type="button" class="dpa-btn dpa-btn--primary" (click)="applyWindow()" [disabled]="loading">
            Show
          </button>
        </ng-container>
      </div>
    </div>

    <!-- A SECTION of the masthead, under a hairline: the same card, a quieter
         voice. These measure the date, and only over the stays that had one. -->
    <div class="dpa-masthead__sub" *ngIf="!loading && !error && report">
      <p class="dpa-band__cap" id="dpa-date-cap">When a date was documented, how accurate was it?</p>
      <dl class="dpa-figs dpa-figs--quiet" aria-labelledby="dpa-date-cap">
        <div class="dpa-fig" *ngFor="let figure of figures; trackBy: trackByFigure"
          [class.dpa-fig--good]="figure.tone === 'good'"
          [class.dpa-fig--warn]="figure.tone === 'warn'">
          <dt>{{ figure.label }}</dt>
          <dd>{{ figure.value }}</dd>
          <dd class="dpa-fig__sub">{{ figure.sub }}</dd>
        </div>
      </dl>
    </div>

    <!-- The third tier: which buildings. The Discharge Plan screen's chips, so
         the two screens share one gesture. Every chip on is "all"; the count is
         the stays in the window, the denominator behind every percentage above.
         A building with none still has a chip, reading 0 - here that is the
         finding. Absent for a reader with one building. -->
    <div class="dpa-masthead__chips" *ngIf="!loading && !error && report && showFacilityChips"
      role="group" aria-label="Facilities">
      <span class="dpa-chips__cap">Facilities</span>
      <button type="button" class="dpa-chip" *ngFor="let chip of visibleChips; trackBy: trackByChip"
        [class.is-on]="activeFacilityIds.has(chip.facilityId)"
        [class.is-empty]="chip.stays === 0"
        [attr.aria-pressed]="activeFacilityIds.has(chip.facilityId)"
        [attr.aria-label]="chip.name + ', ' + chip.stays + (chip.stays === 1 ? ' stay' : ' stays')"
        (click)="toggleFacility(chip.facilityId)">
        <span class="dpa-tick" aria-hidden="true"></span>
        <span class="dpa-chip__name" [title]="chip.name">{{ chip.name }}</span>
        <span class="dpa-chip__count" aria-hidden="true">{{ chip.stays }}</span>
      </button>
      <button type="button" class="dpa-link" *ngIf="hasFoldedChips" (click)="toggleChipFold()"
        [attr.aria-expanded]="chipsExpanded">{{ chipsExpanded ? 'Show fewer' : 'Show all (' + facilityCount + ')' }}</button>
      <!-- Both ways at once: everything on, or nothing on and pick from
           there. Each hides when it would change nothing. -->
      <button type="button" class="dpa-link" *ngIf="!allFacilitiesSelected" (click)="selectAllFacilities()">Select all</button>
      <button type="button" class="dpa-link" *ngIf="activeFacilityIds.size > 0" (click)="clearFacilities()">Clear all</button>
    </div>
  </div>

  <!-- ============ STATES ================================================ -->
  <p class="dpa-state" *ngIf="loading" role="status">Loading the report&hellip;</p>

  <p class="dpa-state dpa-state--error" *ngIf="!loading && error" role="alert">{{ error }}</p>

  <div class="dpa-notice" *ngIf="!loading && !error && report?.truncated" role="status">
    This window exceeds what one report can carry, so the oldest entries are not included. The figures below are minimums, not totals. Choose a shorter window.
  </div>

  <!-- The record began inside the window asked for. Before it there was no
       board to read, and a discharge from then would show as a surprise for
       want of a record, not of planning - so the report starts later and says
       where. Quiet, not amber: this is a fact about the data, not a fault. -->
  <div class="dpa-notice dpa-notice--record" *ngIf="!loading && !error && hasRows && recordNote" role="status">{{ recordNote }}</div>

  <!-- ============ THE BUILDINGS, COMPARED ============================== -->
  <!-- The question the report was asked, answered per building before the
       reader digs into rows: who saw their discharges coming, who was
       surprised. Every building stays on the strip - an unticked one is
       dimmed, not dropped, because a comparison with buildings missing is not
       one. The chips below narrow the rows and the bands; this only orders. -->
  <section class="dpa-compare" *ngIf="!loading && !error && report && showCompare">
    <!-- The caption bar stays when the strip is folded, so the way back is
         where the strip was. -->
    <div class="dpa-compare__bar">
      <span class="dpa-compare__cap">By facility</span>
      <span class="dpa-compare__hint" *ngIf="!compareCollapsed">{{ compareHint }}</span>
      <span class="dpa-compare__actions">
        <!-- The way back, where the narrowing happened: the same "all" the
             chips offer, so the reader does not have to find it up there. -->
        <button type="button" class="dpa-link" *ngIf="isFacilityFiltered" (click)="selectAllFacilities()">All facilities</button>
        <button type="button" class="dpa-link" (click)="toggleCompare()"
          [attr.aria-expanded]="!compareCollapsed" aria-controls="dpa-compare-table">{{ compareCollapsed ? 'Show' : 'Hide' }}</button>
      </span>
    </div>

    <div id="dpa-compare-table" role="table" aria-label="Facilities compared" *ngIf="!compareCollapsed">
    <div class="dpa-compare__head" role="row">
      <div role="columnheader" [attr.aria-sort]="compareAriaSort('facility')">
        <button type="button" class="dpa-sort" (click)="setCompareSort('facility')">Facility<span
          class="dpa-sort__arrow" aria-hidden="true">{{ compareArrow('facility') }}</span></button>
      </div>
      <div class="dpa-num dpa-compare__opt" role="columnheader" [attr.aria-sort]="compareAriaSort('stays')">
        <button type="button" class="dpa-sort" (click)="setCompareSort('stays')">Discharges<span
          class="dpa-sort__arrow" aria-hidden="true">{{ compareArrow('stays') }}</span></button>
      </div>
      <div class="dpa-num" role="columnheader" [attr.aria-sort]="compareAriaSort('plannedAhead')">
        <button type="button" class="dpa-sort" (click)="setCompareSort('plannedAhead')">Anticipated<span
          class="dpa-sort__arrow" aria-hidden="true">{{ compareArrow('plannedAhead') }}</span></button>
      </div>
      <!-- The bar's header is its legend: the three things a discharge can be,
           in the three colours the bar and the pills below share. -->
      <div class="dpa-compare__opt dpa-legend" role="columnheader" aria-label="How each building's discharges split">
        <span class="dpa-legend__item"><i class="dpa-legend__dot dpa-legend__dot--ahead" aria-hidden="true"></i>Anticipated</span>
        <span class="dpa-legend__item"><i class="dpa-legend__dot dpa-legend__dot--missed" aria-hidden="true"></i>Late or incorrect</span>
        <span class="dpa-legend__item"><i class="dpa-legend__dot dpa-legend__dot--none" aria-hidden="true"></i>No plan</span>
      </div>
      <div class="dpa-num dpa-compare__opt" role="columnheader" [attr.aria-sort]="compareAriaSort('notice')">
        <button type="button" class="dpa-sort" (click)="setCompareSort('notice')">Avg notice<span
          class="dpa-sort__arrow" aria-hidden="true">{{ compareArrow('notice') }}</span></button>
      </div>
      <div class="dpa-num" role="columnheader" [attr.aria-sort]="compareAriaSort('surprises')">
        <button type="button" class="dpa-sort" (click)="setCompareSort('surprises')">Unanticipated<span
          class="dpa-sort__arrow" aria-hidden="true">{{ compareArrow('surprises') }}</span></button>
      </div>
    </div>

    <!-- The whole line is the way in: only this building's rows below, and
         the way back out when it is already alone. The click lives on the
         row; the name stays a real button for the keyboard, and its own click
         simply bubbles up to the row - one handler, never a double toggle. -->
    <div class="dpa-compare__row" role="row" *ngFor="let line of compareRows; trackBy: trackByCompareRow"
      [class.is-off]="!line.selected" [class.is-empty]="line.stays === 0" [class.is-solo]="isSoloed(line.facilityId)"
      (click)="focusFacility(line.facilityId)">
      <div class="dpa-compare__name" role="cell">
        <button type="button" class="dpa-compare__pick" [title]="line.name"
          [attr.aria-pressed]="isSoloed(line.facilityId)"
          [attr.aria-label]="isSoloed(line.facilityId) ? 'Show all facilities' : 'Show only ' + line.name">{{ line.name }}</button>
      </div>
      <div class="dpa-num dpa-compare__opt" role="cell">{{ line.stays }}</div>
      <div class="dpa-num dpa-compare__lead" role="cell">{{ line.plannedAheadLabel }}</div>
      <!-- The building's discharges as one bar: what the board showed, what
           it did not, what was never written. Read at a glance across the
           strip, which four columns of numbers never were. -->
      <div class="dpa-compare__opt dpa-bar" role="cell" [attr.title]="line.barLabel" [attr.aria-label]="line.barLabel">
        <span class="dpa-bar__seg dpa-bar__seg--ahead" [style.width.%]="line.aheadPercent" aria-hidden="true"></span>
        <span class="dpa-bar__seg dpa-bar__seg--missed" [style.width.%]="line.missedPercent" aria-hidden="true"></span>
        <span class="dpa-bar__seg dpa-bar__seg--none" [style.width.%]="line.noPlanPercent" aria-hidden="true"></span>
      </div>
      <div class="dpa-num dpa-compare__opt" role="cell">{{ line.noticeLabel }}</div>
      <div class="dpa-num" role="cell">{{ line.surprises }}</div>
    </div>
    </div>
  </section>

  <!-- Where the rows begin, for the strip to scroll to. Always present, so a
       pick that lands on the empty state scrolls just the same. -->
  <div #staysAnchor class="dpa-anchor" aria-hidden="true"></div>


  <!-- ============ THE STAYS ============================================= -->
  <!-- A table to a screen reader, all the way down: the header claims row and
       columnheader (so aria-sort means something), and a row or columnheader
       with no table above it and no cells beside it is a shape a reader
       drops. So the section is the table, every stay is a row of cells, and
       an open log is one more row spanning the width. -->
  <!-- One list or one card per building: the Discharge Plan screen's own
       pair of layouts, offered here only when there is more than one
       building to split by. -->
  <div class="dpa-layout" *ngIf="!loading && !error && hasRows && facilityChips.length > 1">
    <div class="dpa-seg" role="group" aria-label="Rows layout">
      <button type="button" *ngFor="let option of layoutModes; trackBy: trackByLayout"
        class="dpa-seg__btn" [class.is-on]="layout === option.id"
        [attr.aria-pressed]="layout === option.id"
        (click)="setLayout(option.id)">{{ option.label }}</button>
    </div>
  </div>

  <ng-container *ngIf="!loading && !error && hasRows">
    <ng-container *ngIf="layout === 'all'; else byFacility">
      <ng-container *ngTemplateOutlet="stayTable; context: { rows: rows, label: 'Stays in this window' }"></ng-container>
    </ng-container>
    <ng-template #byFacility>
      <div class="dpa-group" *ngFor="let group of rowGroups; trackBy: trackByGroup">
        <h2 class="dpa-group__head">
          <span class="dpa-group__name">{{ group.name }}</span>
          <span class="dpa-group__meta">{{ group.rows.length }} {{ group.rows.length === 1 ? 'discharge' : 'discharges' }}<ng-container
            *ngIf="groupLead(group) as lead"> &middot; {{ lead }} anticipated</ng-container></span>
        </h2>
        <ng-container *ngTemplateOutlet="stayTable; context: { rows: group.rows, label: group.name + ': stays in this window' }"></ng-container>
      </div>
    </ng-template>
  </ng-container>

  <!-- The table itself, once: the head and the rows, fed either every row or
       one building's. -->
  <ng-template #stayTable let-list="rows" let-label="label">
  <section class="dpa-rows" role="table" [attr.aria-label]="label">
    <!-- Sortable, so no longer aria-hidden: these are real column headers the
         reader can operate, and a screen reader has to be able to reach them. -->
    <div class="dpa-head" role="row">
      <!-- One column, two orders: the name, or the building under it - the
           list runs across every ticked facility, and gathering one
           building's rows together is not the same as narrowing to it. -->
      <div role="columnheader" [attr.aria-sort]="ariaSortOf('patient') || ariaSortOf('facility')">
        <button type="button" class="dpa-sort" (click)="setSort('patient')">Patient<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('patient') }}</span></button>
        <span class="dpa-sort__sep" aria-hidden="true">&middot;</span>
        <button type="button" class="dpa-sort" (click)="setSort('facility')">Facility<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('facility') }}</span></button>
      </div>
      <div role="columnheader" [attr.aria-sort]="ariaSortOf('expected')">
        <button type="button" class="dpa-sort" (click)="setSort('expected')" title="The last expected date documented before the discharge">Planned discharge<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('expected') }}</span></button>
      </div>
      <div class="dpa-num dpa-day" role="columnheader" [attr.aria-sort]="ariaSortOf('discharged')">
        <button type="button" class="dpa-sort" (click)="setSort('discharged')" title="Day the patient actually left">Actual discharge<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('discharged') }}</span></button>
      </div>
      <div class="dpa-num dpa-day" role="columnheader" [attr.aria-sort]="ariaSortOf('admitted')">
        <button type="button" class="dpa-sort" (click)="setSort('admitted')" title="Day the patient was admitted">Admitted<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('admitted') }}</span></button>
      </div>
      <div class="dpa-num dpa-stay" role="columnheader" [attr.aria-sort]="ariaSortOf('stay')">
        <button type="button" class="dpa-sort" (click)="setSort('stay')" title="Length of stay, in days">LOS<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('stay') }}</span></button>
      </div>
      <div class="dpa-num dpa-day" role="columnheader" [attr.aria-sort]="ariaSortOf('plannedOn')">
        <button type="button" class="dpa-sort" (click)="setSort('plannedOn')" title="Day the first expected date was documented">Documented on<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('plannedOn') }}</span></button>
      </div>
      <div class="dpa-by" role="columnheader" [attr.aria-sort]="ariaSortOf('setBy')">
        <button type="button" class="dpa-sort" (click)="setSort('setBy')" title="Who documented the first expected date">Documented by<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('setBy') }}</span></button>
      </div>
      <div role="columnheader" [attr.aria-sort]="ariaSortOf('notice')">
        <button type="button" class="dpa-sort" (click)="setSort('notice')" title="How far ahead the plan showed the discharge coming, when it did">Advance notice<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('notice') }}</span></button>
      </div>
      <div class="dpa-num" role="columnheader" [attr.aria-sort]="ariaSortOf('changes')">
        <button type="button" class="dpa-sort" (click)="setSort('changes')" title="How many times the date itself was changed; note edits do not count">Changes<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('changes') }}</span></button>
      </div>
      <div class="dpa-num" role="columnheader" [attr.aria-sort]="ariaSortOf('daysOff')">
        <button type="button" class="dpa-sort" (click)="setSort('daysOff')" title="Days between the planned and the actual discharge; plus means the patient stayed longer">Variance<span
          class="dpa-sort__arrow" aria-hidden="true">{{ arrowOf('daysOff') }}</span></button>
      </div>
      <div role="columnheader" aria-label="Log"></div>
    </div>

    <ng-container *ngFor="let row of list; trackBy: trackByKey">
      <div class="dpa-row" role="row" [class.is-open]="isOpen(row)">
        <div class="dpa-who" role="cell">
          <div class="dpa-who__name">{{ row.patientName || 'Unnamed patient' }}</div>
          <div class="dpa-who__where">{{ row.facilityName }}</div>
        </div>

        <div class="dpa-trail" role="cell">
          <!-- A discharge nobody wrote a date for still has a row, and the cell
               has to say why it is empty rather than just look broken. -->
          <span class="dpa-trail__none" *ngIf="row.trail.length === 0">Not documented</span>
          <ng-container *ngFor="let step of row.trail; let i = index; trackBy: trackByIndex">
            <span class="dpa-arrow" aria-hidden="true" *ngIf="i > 0">&rarr;</span>
            <span class="dpa-date"
              [class.dpa-date--last]="i === row.trail.length - 1"
              [class.dpa-date--cleared]="row.cleared && i === row.trail.length - 1">{{ step.label }}</span>
          </ng-container>
          <span class="dpa-flag" *ngIf="row.cleared">Withdrawn</span>
        </div>

        <div class="dpa-num dpa-day" role="cell">{{ row.dischargedLabel || '—' }}</div>
        <div class="dpa-num dpa-day" role="cell">{{ row.admittedLabel || '—' }}</div>
        <div class="dpa-num dpa-stay" role="cell" [class.dpa-muted]="!row.stayLabel">{{ row.stayLabel || '—' }}</div>
        <div class="dpa-num dpa-day" role="cell">{{ row.plannedOnLabel || '—' }}</div>
        <div class="dpa-by" role="cell" [class.dpa-muted]="!row.setBy">{{ row.setBy || '—' }}</div>

        <div class="dpa-lead" role="cell">
          <span class="dpa-pill" [ngClass]="'dpa-pill--' + row.lead">{{ row.leadLabel }}</span>
        </div>

        <div class="dpa-num dpa-moves" role="cell"
          [class.dpa-moves--none]="row.timesDateMoved === 0"
          [class.dpa-moves--many]="row.timesDateMoved > 1">{{ row.timesDateMoved }}</div>

        <div class="dpa-num dpa-var" role="cell" [ngClass]="'dpa-var--' + row.outcome">{{ row.varianceLabel }}</div>

        <!-- Every row with a trail gets the chevron, a single save included:
             the owner wants one place that always opens the log, even when it
             holds one line (when, what, by whom, and the note). Only a stay
             nobody ever planned has nothing to open; its cell stays, so the
             row keeps its shape. -->
        <div class="dpa-toggle-cell" role="cell">
          <button type="button" class="dpa-toggle" *ngIf="row.changes.length > 0" (click)="toggle(row)"
            [attr.aria-expanded]="isOpen(row)" [attr.aria-controls]="'dpa-log-' + row.key"
            [attr.aria-label]="(isOpen(row) ? 'Hide' : 'Show') + ' the full log for ' + row.patientName">
            <span class="dpa-toggle__chev" aria-hidden="true">&rsaquo;</span>
          </button>
        </div>
      </div>

      <div class="dpa-log" role="row" [id]="'dpa-log-' + row.key" *ngIf="isOpen(row)">
        <div role="cell" aria-colspan="11">
          <ol>
            <li *ngFor="let change of row.changes; trackBy: trackByIndex">
              <span class="dpa-log__when">{{ change.when }}</span>
              <span class="dpa-log__what">{{ change.what }}</span>
              <span class="dpa-log__by">{{ change.by }}</span>
              <!-- The note on its own line, labelled: appended after the name in
                   grey it went unread. -->
              <span class="dpa-log__note" *ngIf="change.note"><span class="dpa-log__note-cap">Note</span>{{ change.note }}</span>
            </li>
          </ol>
        </div>
      </div>
    </ng-container>
  </section>
  </ng-template>

  <div class="dpa-empty" *ngIf="!loading && !error && report && !hasRows">
    <!-- Four different nothings: no plan has ever been recorded; the record
         began after the window, or too late in it to hold a discharge; a
         window with no discharge anywhere; and a selection of buildings with
         none while the rest of the window has some. None may read as another,
         or the reader blames the EHR, or the buildings. -->
    <ng-container *ngIf="!hasNoFacilitySelected && hasNoRecord">
      <p class="dpa-empty__lead">No discharge plans have been recorded yet.</p>
      <p class="dpa-empty__sub">
        This report reads each discharge against the Discharge Plan as it stood two days before. It fills in from the first documented plan, once patients with a plan are discharged.
      </p>
    </ng-container>
    <ng-container *ngIf="!hasNoFacilitySelected && !hasNoRecord && isBeforeRecord">
      <p class="dpa-empty__lead">Nothing to report in this window yet.</p>
      <p class="dpa-empty__sub">
        Plans have been recorded since {{ report.historySince }}. The report covers the discharges from two days after that, the first day with a plan to read against, and there are none in this window so far.
      </p>
    </ng-container>
    <ng-container *ngIf="hasNoFacilitySelected">
      <p class="dpa-empty__lead">No facility selected.</p>
      <p class="dpa-empty__sub">Select facilities above, or select them all.</p>
      <button type="button" class="dpa-btn" (click)="selectAllFacilities()">Select all facilities</button>
    </ng-container>
    <ng-container *ngIf="!hasNoFacilitySelected && !hasNoRecord && !isBeforeRecord && !hasHiddenRows">
      <p class="dpa-empty__lead">No discharges in this window.</p>
      <p class="dpa-empty__sub">
        This report lists the stays that ended between the two dates above, planned or not. An empty result means no discharge was mirrored from the EHR in that range, not that no plans were documented.
      </p>
    </ng-container>
    <ng-container *ngIf="!hasNoFacilitySelected && hasHiddenRows">
      <p class="dpa-empty__lead">No discharges at {{ selectedFacilityLabel }} in this window.</p>
      <p class="dpa-empty__sub">
        The other facilities in your access had {{ report.rows.length }}
        {{ report.rows.length === 1 ? 'discharge' : 'discharges' }} in the same window.
      </p>
      <button type="button" class="dpa-btn" (click)="selectAllFacilities()">Select all facilities</button>
    </ng-container>
  </div>

  <!-- ============ WHAT THE COLUMNS MEAN ================================= -->
  <!-- A glossary, term by term, in the columns' own order: one line each,
       not a paragraph the reader has to mine. -->
  <dl class="dpa-foot" *ngIf="!loading && !error && hasRows">
    <div class="dpa-foot__row"><dt>Anticipated</dt><dd>Two days before the discharge the plan showed a date no later than a day after the actual one. A past-due date still counts.</dd></div>
    <div class="dpa-foot__row"><dt>Late plan</dt><dd>Nothing was on the plan two days before: the date was documented inside the last two days, or withdrawn earlier.</dd></div>
    <div class="dpa-foot__row"><dt>Planned for</dt><dd>The plan showed a date more than a day past the actual discharge.</dd></div>
    <div class="dpa-foot__row"><dt>No plan</dt><dd>No expected discharge date was ever documented for the stay.</dd></div>
    <div class="dpa-foot__row"><dt>Unanticipated</dt><dd>Late plan, Planned for and No plan together: the discharges the facility did not anticipate two days ahead.</dd></div>
    <div class="dpa-foot__row"><dt>Planned discharge</dt><dd>The last expected date documented before the discharge.</dd></div>
    <div class="dpa-foot__row"><dt>Documented on / by</dt><dd>When the first expected date was documented, and by whom. For the record only.</dd></div>
    <div class="dpa-foot__row"><dt>Advance notice</dt><dd>Days from when the plan first showed that date, and kept showing it, to the discharge.<ng-container *ngIf="report?.noticeCapDays"> The average counts at most {{ report?.noticeCapDays }} days per stay.</ng-container></dd></div>
    <div class="dpa-foot__row"><dt>Changes</dt><dd>How many times the date itself moved. Note edits stay in the log and are not counted.</dd></div>
    <div class="dpa-foot__row"><dt>Variance</dt><dd>Actual discharge minus planned discharge, in days. Plus means the patient stayed longer.</dd></div>
    <div class="dpa-foot__row"><dt>Rows</dt><dd>Every row is an actual discharge; patients still admitted are not listed. The record keeps one current stay per patient, so a stay readmitted and discharged again after this window may be missing, or read against the later stay.</dd></div>
  </dl>
</div>
